import { ImageService } from './services/image.service';
import { TransitionService } from './services/transition.service';
import { ImgproxyService } from './services/imgproxy.service';
export class Blinkloader {
    constructor(config) {
        this.config = {
            valid: false,
            cdnBase: 'https://cdn.a-it.org',
            lazyload: true,
            batchSize: 5,
            batchInterval: 250,
            responsive: true,
            webp: true,
            retina: true,
            fadeIn: false,
        };
        this.pendingImages = [];
        this.isProcessing = false;
        this.optimize(config);
        return this;
    }
    optimize(config) {
        try {
            this.validateAndApplyConfig(config);
            this.imgproxyService = new ImgproxyService(this.config.cdnBase, this.config);
            this.initializeEventListeners();
            this.startImageProcessing();
        }
        catch (error) {
            console.error('Blinkloader: Failed to initialize', error);
        }
    }
    validateAndApplyConfig(config) {
        if (!config) {
            throw new Error('Missing configuration');
        }
        this.config = {
            ...this.config,
            ...config,
            valid: true,
        };
    }
    initializeEventListeners() {
        // Listen for scroll events
        window.addEventListener('scroll', () => this.updateImages());
        // Listen for window resize events
        window.addEventListener('resize', this.debounce(() => {
            this.updateImages();
        }, 250));
        // Setup MutationObserver for DOM changes
        const observer = new MutationObserver(this.debounce(() => {
            this.updateImages();
        }, 250));
        // Observe DOM changes
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            // attributeFilter: ['imgproxy-src']
        });
        // Initial query
        this.updateImages();
        setTimeout(() => this.updateImages(), 250);
        window.addEventListener('DOMContentLoaded', () => this.updateImages());
        window.addEventListener('', () => this.updateImages());
    }
    updateImages() {
        this.queryImages();
        if (!this.isProcessing) {
            window.requestAnimationFrame(() => {
                this.processImages();
                this.isProcessing = false;
            });
            this.isProcessing = true;
        }
    }
    queryImages() {
        this.pendingImages = [];
        const elements = document.querySelectorAll('[imgproxy-src]');
        elements.forEach((element) => {
            if (element instanceof HTMLElement) {
                this.checkAndAddImage(element);
            }
        });
    }
    startImageProcessing() {
        this.processImages();
    }
    async processImages() {
        // Debug point: Start of image batch processing
        if (!this.config.valid || this.pendingImages.length === 0) {
            return;
        }
        for (const image of this.pendingImages) {
            // Debug point: Processing individual image
            const imageService = new ImageService(image, this.imgproxyService);
            try {
                await imageService.render();
                if (this.config.fadeIn) {
                    // Debug point: Before fade-in animation
                    TransitionService.fadeIn(image.node);
                }
            }
            catch (error) {
                // Debug point: Image processing error
                console.error('Failed to process image:', error);
            }
        }
    }
    checkAndAddImage(element) {
        // Debug point: New image check
        const src = element.getAttribute('imgproxy-src');
        if (!src)
            return;
        const imageConfig = {
            src,
            width: this.parseIntAttribute(element, 'imgproxy-width') || element.offsetWidth,
            height: this.parseIntAttribute(element, 'imgproxy-height') || element.offsetHeight,
            quality: this.parseIntAttribute(element, 'imgproxy-quality'),
            format: this.parseFormatAttribute(element, 'imgproxy-format'),
            resizingType: this.parseResizeAttribute(element, 'imgproxy-resize'),
            gravity: this.parseGravityAttribute(element, 'imgproxy-gravity'),
            enlarge: element.hasAttribute('imgproxy-enlarge') ? true : null,
            extend: element.hasAttribute('imgproxy-extend') ? true : null,
        };
        const blinkImage = {
            node: element,
            config: imageConfig,
        };
        // Debug point: Image added to pending queue
        this.pendingImages.push(blinkImage);
    }
    parseIntAttribute(element, attributeName) {
        const value = element.getAttribute(attributeName);
        if (!value)
            return null;
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
    }
    parseFormatAttribute(element, attributeName) {
        const value = element.getAttribute(attributeName);
        if (!value)
            return null;
        return (['jpeg', 'png', 'webp'].includes(value) ? value : null);
    }
    parseResizeAttribute(element, attributeName) {
        const value = element.getAttribute(attributeName);
        if (!value)
            return null;
        return (['fit', 'fill', 'auto'].includes(value) ? value : null);
    }
    parseGravityAttribute(element, attributeName) {
        const value = element.getAttribute(attributeName);
        if (!value)
            return null;
        return (['no', 'so', 'ea', 'we', 'ce', 'sm'].includes(value) ? value : null);
    }
    debounce(func, wait) {
        let timeout;
        return () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(), wait);
        };
    }
}
//# sourceMappingURL=blinkloader.js.map